import React, { createContext, useState, useEffect } from "react";
import rg4js from "raygun4js";
import useRaygun from "@/hooks/useRaygun";
import { isRaygunReportingEnabled } from "@/lib/raygunUtils";

declare global {
  interface Window {
    raygunUserAgentDataStatus?: number;
  }
}

export const RaygunContext = createContext<{ raygunBooted: boolean; raygunApiKey: string }>({
  raygunBooted: false,
  raygunApiKey: ""
});

export function RaygunProvider({ children }: { children: React.ReactNode }) {
  const [raygunBooted, setRaygunBooted] = useState(false);
  const raygunApiKey = import.meta.env.VITE_RAYGUN_API_KEY;
  const shouldBootRaygun =
    Boolean(raygunApiKey) &&
    isRaygunReportingEnabled() &&
    import.meta.env.NODE_ENV !== "test" &&
    !import.meta.env.VITEST;

  useEffect(() => {
    if (!raygunBooted && shouldBootRaygun) {
      rg4js("apiKey", raygunApiKey);
      rg4js("enableCrashReporting", true);
      rg4js("options", { ignore3rdPartyErrors: true });
      setRaygunBooted(true);
    }

    // Cleanup function to prevent window access after test teardown
    return () => {
      if (typeof window !== "undefined") {
        // Clear any pending timeouts that might try to access window
        const timeouts = window.raygunUserAgentDataStatus;
        if (timeouts) {
          delete window.raygunUserAgentDataStatus;
        }
      }
    };
  }, [raygunBooted, raygunApiKey, shouldBootRaygun]);

  return <RaygunContext.Provider value={{ raygunBooted, raygunApiKey }}>{children}</RaygunContext.Provider>;
}

export function InitializeRaygunUser() {
  useRaygun();
  return null;
}
